import { Alert, Box, Typography } from "@mui/material";
import Loader from "components/Loader";
import RedirectBackButton from "components/RedirectBackButton";
import { FORM_ERROR } from "final-form";
import useGlobalContext from "hooks/globalData";
import useTitle from "hooks/useTitle";
import useTrashablePromises from "hooks/useTrashablePromises";
import { FormattedMessage as T, useIntl } from "react-intl";
import { useQuery, useQueryClient } from "react-query";
import { Navigate, useNavigate, useParams } from "react-router-dom";
import {
  MenuRequest,
  ResponseOfMenu,
  ResponseOfMenuUsage,
} from "services/KnowfullyAdminClient";
import translateError from "utils/translateError";
import MenuAccordions from "./MenuAccordions";

const MenuUpdateForm = () => {
  const { registerPromise } = useTrashablePromises();
  const { formatMessage: t } = useIntl();
  const navigate = useNavigate();
  const { menuService, menusUrl, menuDetailsUrl } = useGlobalContext();
  const { id } = useParams<{ id: string }>();
  const queryClient = useQueryClient();

  const menuResult = useQuery<ResponseOfMenu>(
    ["menu", id],
    () => menuService.getMenuById(id!),
    {
      enabled: !!id,
    }
  );

  const menuUsageResult = useQuery<ResponseOfMenuUsage>(
    ["menuService.getMenuUsage", id],
    () => menuService.getMenuUsage(id!),
    {
      enabled: !!id,
    }
  );

  const menu = menuResult.data?.data;
  const assignedOrganizationCount =
    menuUsageResult.data?.data?.assignedOrganizationCount ?? 0;
  useTitle(menu?.name || t({ id: "menuFormEditButton" }));

  const redirectToDetails = () => {
    navigate(menuDetailsUrl(id));
  };

  const onSubmit = async (values: MenuRequest) => {
    try {
      values.id = id;
      var result = await registerPromise(menuService.saveMenu(values));
      queryClient.invalidateQueries("menus");
      queryClient.invalidateQueries(["menu", id]);

      redirectToDetails();
      return result;
    } catch (err: any) {
      return { [FORM_ERROR]: translateError(err, t) };
    }
  };

  if (!id) return <Navigate to={menusUrl()} replace />;
  if (menuResult.isLoading) return <Loader />;
  if (menuResult.isError) {
    return (
      <Box mt={4}>
        <Alert severity="error">
          <T id="menuLoadError" />
        </Alert>
      </Box>
    );
  }

  return (
    <>
      <RedirectBackButton
        redirectRoute={menuDetailsUrl(id)}
        labelKey="backToDetailsButtonLabel"
      />
      <Box my={2}>
        <Typography variant="h5">
          <T id="menuFormEditTitle" />
        </Typography>
      </Box>
      {assignedOrganizationCount > 0 && (
        <Box my={2}>
          <Alert severity="warning">
            <T
              id="menuInUseWarning"
              values={{ count: assignedOrganizationCount }}
            />
          </Alert>
        </Box>
      )}
      <MenuAccordions
        menuId={id}
        entity={menu}
        onSubmit={onSubmit}
        onCancel={redirectToDetails}
        submitLabel={t({ id: "menuFormEditButton" })}
      />
    </>
  );
};

export default MenuUpdateForm;
